import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/ui/date-picker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCreatePayment } from "@/hooks/use-wholesale";
import { fmtKm, parseNumberInput } from "@/lib/wholesale-format";
import type { WholesaleStatus } from "@/lib/wholesale";
import { PAYABLE_STATUSES } from "./constants";

const NO_ORDER = "__none__";

export type PayableOrder = { id: string; number: string; total: number; paid: number; status: WholesaleStatus };

function todayIso(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** "2026-03-05" → unix sekunde lokalnog podneva, da vremenska zona ne pomjeri dan. */
function isoToUnix(iso: string): number {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return Math.floor(Date.now() / 1000);
  return Math.floor(new Date(y, m - 1, d, 12, 0, 0).getTime() / 1000);
}

export function PaymentForm({
  partnerId,
  orders = [],
  defaultOrderId,
  onDone,
  onCancel,
  embedded = false,
}: {
  partnerId: string;
  orders?: PayableOrder[];
  defaultOrderId?: string;
  onDone?: () => void;
  onCancel?: () => void;
  /** `true` kad forma već sjedi u kartici: bez okvira i vlastitog naslova. */
  embedded?: boolean;
}) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState<"bank" | "cash">("bank");
  const [paidAt, setPaidAt] = useState(todayIso());
  const [orderId, setOrderId] = useState(defaultOrderId ?? NO_ORDER);
  const [note, setNote] = useState("");
  const create = useCreatePayment();
  // Nacrt i otkazana narudžba nemaju dug, pa se na njih ne knjiži uplata.
  const payable = orders.filter((o) => PAYABLE_STATUSES.includes(o.status));

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const value = parseNumberInput(amount);
    if (value === null || value <= 0) {
      toast.error("Iznos mora biti veći od 0");
      return;
    }
    try {
      await create.mutateAsync({
        partnerId,
        orderId: orderId === NO_ORDER ? null : orderId,
        amount: value,
        method,
        paidAt: isoToUnix(paidAt),
        note: note.trim(),
      });
      toast.success("Uplata evidentirana");
      setAmount("");
      setNote("");
      onDone?.();
    } catch (err: any) {
      toast.error(err?.message || "Greška pri evidentiranju uplate");
    }
  }

  return (
    <form onSubmit={handleSubmit} className={embedded ? "flex flex-col gap-2" : "flex flex-col gap-2 ring-1 ring-foreground/10 p-3"}>
      {!embedded && <p className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Nova uplata</p>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div className="flex flex-col gap-1">
          <Label htmlFor="payment-amount" className="text-xs">Iznos</Label>
          <Input
            id="payment-amount"
            className="h-8 text-xs tabular-nums"
            inputMode="decimal"
            autoFocus={embedded}
            placeholder="0,00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="payment-method" className="text-xs">Način</Label>
          <Select value={method} onValueChange={(v) => setMethod(v as "bank" | "cash")}>
            <SelectTrigger id="payment-method" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="bank">Virman</SelectItem>
              <SelectItem value="cash">Gotovina</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-xs">Datum</Label>
          <DatePicker value={paidAt} onChange={setPaidAt} className="h-8 text-xs w-full" />
        </div>
        {payable.length > 0 && (
          <div className="flex flex-col gap-1">
            <Label htmlFor="payment-order" className="text-xs">Narudžba</Label>
            <Select value={orderId} onValueChange={setOrderId}>
              <SelectTrigger id="payment-order" className="w-full">
                <SelectValue placeholder="Bez narudžbe" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_ORDER}>Bez narudžbe</SelectItem>
                {payable.map((o) => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.number} — {fmtKm(o.total - o.paid)} preostalo
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="flex flex-col gap-1 sm:col-span-2">
          <Label htmlFor="payment-note" className="text-xs">Napomena</Label>
          <Input id="payment-note" className="h-8 text-xs" value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        {onCancel && (
          <Button type="button" variant="outline" size="sm" onClick={onCancel}>Odustani</Button>
        )}
        <Button type="submit" size="sm" disabled={create.isPending}>
          {create.isPending ? "Spremanje…" : "Evidentiraj uplatu"}
        </Button>
      </div>
    </form>
  );
}
